import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';
import { AppContext } from '../../contexts/AppContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Snackbar from '@mui/material/Snackbar';
import Divider from '@mui/material/Divider';

interface InviteReviewerDialogProps {
  open: boolean;
  onClose: () => void;
  planId: string;
  planOwnerId: string;
}

const InviteReviewerDialog: React.FC<InviteReviewerDialogProps> = observer(({ open, onClose, planId, planOwnerId }) => {
  const intl = useIntl();
  const { reviewService } = React.useContext(AppContext);

  const [email, setEmail] = React.useState('');
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      reviewService.loadInvitesForPlan(planId, planOwnerId);
      reviewService.clearError();
    }
  }, [open, planId, planOwnerId, reviewService]);

  const handleSendInvite = async () => {
    if (!email.trim()) return;
    try {
      await reviewService.sendInvite(planId, planOwnerId, email.trim().toLowerCase());
      setEmail('');
      setShowSuccess(true);
    } catch (_e) {
      setShowError(true);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    await reviewService.revokeInvite(inviteId);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          {intl.formatMessage({ id: 'review.inviteDialogTitle' })}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" gap={1} mt={1}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={intl.formatMessage({ id: 'review.emailPlaceholder' })}
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendInvite()}
              type="email"
              disabled={reviewService.isSendingInvite}
            />
            <Button
              variant="contained"
              onClick={handleSendInvite}
              disabled={!email.trim() || reviewService.isSendingInvite}
              sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
            >
              {reviewService.isSendingInvite
                ? <CircularProgress size={18} color="inherit" />
                : intl.formatMessage({ id: 'review.sendInvite' })}
            </Button>
          </Box>

          {reviewService.invites.length > 0 && (
            <Box mt={2}>
              <Divider />
              <Box mt={1}>
                {reviewService.invites.map(invite => (
                  <Box key={invite.id} display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
                    <Box>
                      <Typography variant="body2">{invite.reviewerEmail}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={intl.formatMessage({ id: invite.status === 'accepted' ? 'review.accepted' : 'review.pending' })}
                        size="small"
                        color={invite.status === 'accepted' ? 'success' : 'default'}
                        variant="outlined"
                      />
                      <IconButton size="small" onClick={() => handleRevoke(invite.id)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{intl.formatMessage({ id: 'action.close' })}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={intl.formatMessage({ id: 'review.inviteSent' })}
        autoHideDuration={3000}
        ContentProps={{ sx: { background: '#ffffff', color: '#000000' } }}
      />
      <Snackbar
        open={showError}
        onClose={() => setShowError(false)}
        message={intl.formatMessage({ id: 'review.inviteError' })}
        autoHideDuration={5000}
        ContentProps={{ sx: { background: '#e51f1f' } }}
      />
    </>
  );
});

export default InviteReviewerDialog;
